import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl, ValidationErrors } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { UserService } from '../../../../../services/user.service';
import { NotificationService } from '../../../../../services/notification.service';
import { Router } from '@angular/router';
import { BulkUploadService } from '../../../../../services/bulk-upload.service';
import { AuthorizationService } from 'src/app/services/authorization.service';

@Component({
  selector: 'app-register-users',
  templateUrl: './register-users.component.html',
  styleUrls: ['./register-users.component.scss']
})
export class RegisterUsersComponent implements OnInit {

  registrationForm: FormGroup;
  submitRegistration: boolean = false;
  loader: boolean = false;
  grades: any[] = [];
  userId: string = '';
  actionType: string = 'add';
  selectedRoleVar: string = '';
  selectedOrgVar: any = null;
  orgAdminOrganisation: any = null;
  orgAdminOrganisationType: any = null;

  @Input() roles: any = [];
  @Input() organisation: any[] = [];
  @Input() organizationType: any[] = [];
  @Input() editUserData: any = null;

  @Output() closePopup = new EventEmitter();
  @Output() userAdded = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private ns: NotificationService,
    private router: Router,
    private bulkUpload: BulkUploadService,
    public perm: AuthorizationService
  ) { }

  ngOnInit() {
  }

  /**
   * To handle changes in input properties and initialize form data accordingly.  
   */
  ngOnChanges(changes) {
    //FOR EDIT USER
    this.userId = '';
    this.validateRegistrationForm();
    if ('editUserData' in changes && changes['editUserData'].currentValue !== null) {
      this.actionType = 'edit';
      this.setEditData(this.editUserData);
    }

    //FOR ADD USER BY ORG ADMIN
    if (!this.perm.isSuperAdmin()) {
      if (this.actionType == 'add') {
        // set default organization of org admin
        if (this.organisation.length > 0) {
          this.orgAdminOrganisation = this.organisation[0];
          this.registrationForm.get('organisation').setValue(this.orgAdminOrganisation.slug);
          this.selectedOrgVar = this.orgAdminOrganisation;
        }

        // set default organization type of org admin
        if (this.organizationType.length > 0) {
          this.orgAdminOrganisationType = this.organizationType[0];
          this.registrationForm.get('org_type').setValue(this.orgAdminOrganisationType.slug);
        }
      }
    }
  }

  /**
   * To fetch organizations from the NotificationService.
   */
  getOrganisation() {
    this.ns.getOrganisation().subscribe(orgnosation_list => {
      this.organisation = orgnosation_list.data;
    })
  }

  /**
   * To fetch organization types from the BulkUploadService.
   */
  getOrganizationType() {
    this.bulkUpload.getOrganizationType().subscribe(organozation => {
      this.organizationType = organozation;
    })
  }

  /**
   * To initialize and validate the registration form.
   */
  validateRegistrationForm(): void {
    this.registrationForm = this.formBuilder.group({
      username: [''],
      first_name: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        Validators.pattern('^[a-zA-Z]*$')]],
      last_name: ['', [Validators.required, Validators.maxLength(100), Validators.pattern('^[a-zA-Z]*$')]],
      email: ['', [Validators.required,
      Validators.email,
      Validators.maxLength(280),
      Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')]],
      role: [''],
      organisation: [''],
      org_type: [''],
      grade: [''],
      linkedin_url: ['', [Validators.required, Validators.pattern("^https:\/\/www\.linkedin\.com\/in\/.*")]],
      password: ['', [Validators.required, this.passwordValidator, Validators.minLength(8), Validators.maxLength(30)]],
      userConfirmPassword: ['', [Validators.required]]
    },
    {
      validator: this.userService.MustMatch('password', 'userConfirmPassword')
    });
  }

  /**
   * To validate password complexity.
   * @param control FormControl
   * @returns true if password meets complexity requirements, else returns an error object.
   */
  passwordValidator(control: FormControl): ValidationErrors | null {
    const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#])/;
    const passwordError = { passwordPattern: true }
    if (control.value.length == 0) {
      return null;
    }
    return passwordRegex.test(control.value) ? null : passwordError;
  }

  /**
   * Fetches roles from the UserService and assigns them to the roles property.
   */
  getRoles() {
    this.userService
      .getRoles()
      .subscribe(
        (data) => {
          this.roles = data.roles_info;
        }
      );
  }

  /**
   * Handles role selection and updates grade options based on selected organization type.
   * @param e Event containing selected role value.
   */
  selectedRole(e: any) {
    this.selectedRoleVar = e.target.value;
    if (this.selectedOrgVar && this.selectedOrgVar.organisation_type) {
      this.selectType(this.selectedOrgVar.organisation_type.slug);
    }
  }

  /**
   * Sets grade options based on organization type and selected role.
   * Used for both add and edit user flows.
   * @param type Organization type slug.
   * @param editData Optional user data for edit mode.
   */
  selectType(type: string, editData: any = null) {
    if (!type || type.trim() === '') {
      this.grades = [];
      this.registrationForm.get('grade').setValue('');
      return;
    }

    this.grades = [];
    const n = type === 'school' ? 12 : 6;

    if (this.selectedRoleVar === 'student') {
      for (let i = 1; i <= n; i++) {
        this.grades.push({ name: i, value: i });
      }
      // If in edit mode and editData has grade, set it
      if (editData && editData.grade) {
        this.registrationForm.get('grade').setValue(editData.grade);
      } else {
        this.registrationForm.get('grade').setValue('');
      }
    } else {
      this.registrationForm.get('grade').setValue(1);
    }
  }

  /**
   * Handles organization selection and updates organization type and grade options.
   * @param e Event containing selected organization value.
   */
  selectedOrg(e: any) {
    this.updateOrgType(e.target.value);
  }

  /**
   * Updates selected organization variable, organization type, and grade options.
   * @param value Selected organization slug.
   */
  updateOrgType(value: any) {
    this.selectedOrgVar = this.organisation.find(org => org.slug === value);
    if (this.selectedOrgVar && this.selectedOrgVar.organisation_type) {
      this.registrationForm.get('org_type').setValue(this.selectedOrgVar.organisation_type.slug);
      this.selectType(this.selectedOrgVar.organisation_type.slug);
    } else {
      this.registrationForm.get('org_type').setValue('');
      this.grades = [];
      this.registrationForm.get('grade').setValue('');
    }
  }

  get userRegistration() { return this.registrationForm; }

  /**
   * To handle user registration or editing based on action type.
   * @returns Handles user registration or editing based on action type.
   */
  onRegistration(): void {
    this.submitRegistration = true;

    const formValue = this.registrationForm.value;
    this.registrationForm.get('first_name').setValue(formValue.first_name.trim());
    this.registrationForm.get('last_name').setValue(formValue.last_name.trim());

    if (this.registrationForm.invalid) {
      this.ns.showNotification('error', 'All fields are mandatory. Please correct the errors in the form.');
      return;
    }

    if (this.actionType == 'add') {
      const postData = {
        first_name: formValue.first_name,
        last_name: formValue.last_name,
        email: formValue.email,
        password: formValue.password,
        confirm_password: formValue.userConfirmPassword,
        linkedin_url: formValue.linkedin_url
      }
      this.loader = true;
      this.userService
        .userRegistration(postData)
        .pipe(
          finalize(() => {
            this.loader = false;
            this.submitRegistration = false;
          }))
        .subscribe(
          (response) => {
            this.userAdded.emit({ status: true });
            this.ns.showNotification('success', response.message.status);
            this.onClosePopup();
          },
          (error) => this.ns.showNotification('error', error.message)
        );
    }
    else {
      const postData = {
        "user_id": this.userId,
        "role": formValue.role,
        "organisation": formValue.organisation,
        "grade": formValue.grade,
        "org_type": formValue.org_type,
        "email": formValue.email,
        "password": formValue.password
      }
      this.loader = true;
      this.userService
        .editUserByAdmin(postData)
        .pipe(
          finalize(() => {
            this.loader = false;
            this.submitRegistration = false;
          }))
        .subscribe(
          (response) => {
            this.userAdded.emit({ status: true });
            this.ns.showNotification('success', 'User updated successfully');
            this.onClosePopup();
          },
          (error) => this.ns.showNotification('error', error.message)
        );
    }
  }

  /**
   * To set the form data in case of edit user.
   * @param editUserData Data of the user to be edited.
   */
  setEditData(editUserData) {
    this.registrationForm.get('first_name').setValue('firstname');
    this.registrationForm.get('last_name').setValue('lastname');
    this.registrationForm.get('username').setValue(editUserData.username);
    this.registrationForm.get('password').setValue('');
    this.registrationForm.get('userConfirmPassword').setValue('');

    let org = this.organisation;
    let data = org.find(org_name => org_name.name == editUserData.organisation);
    if (editUserData.email) {
      this.registrationForm.get('email').setValue(editUserData.email);
    }

    if (editUserData.role) {
      this.registrationForm.controls['role'].setValue(editUserData.role);
      this.selectedRoleVar = editUserData.role;
    }

    if (editUserData.organisation) {
      if (data) {
        this.registrationForm.controls['organisation'].setValue(data.slug);
      }
    }

    if (editUserData.institute_type) {
      this.registrationForm.controls['org_type'].setValue(editUserData.institute_type);
    }

    if (editUserData.grade) {
      this.registrationForm.controls['grade'].setValue(editUserData.grade);
    }

    if (editUserData) {
      if (data) {
        this.selectedOrgVar = data;
      }
    }
    this.userId = editUserData.user_id
    this.selectType(editUserData.institute_type, editUserData);
  }

  /**
   * Emits event to close the popup modal.
   */
  onClosePopup() {
    this.closePopup.emit();
  }

}
